import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";

import { useTranslation } from "@/core/i18n";
import { cn } from "@/shadcn/lib/utils";

import { Button } from "../Button/Button";

export type PaginationProps = {
  readonly pageNumber: number;
  readonly totalPages: number;
  readonly totalCount?: number;
  readonly hasPrevious?: boolean;
  readonly hasNext?: boolean;
  readonly disabled?: boolean;
  readonly onPageChange: (page: number) => void;
  readonly className?: string;
  /** Distinguishes the landmark when a page renders more than one pagination. */
  readonly ariaLabel?: string;
};

export const Pagination = React.forwardRef<HTMLElement, PaginationProps>(
  (
    {
      pageNumber,
      totalPages,
      totalCount,
      hasPrevious = pageNumber > 1,
      hasNext = pageNumber < totalPages,
      disabled = false,
      onPageChange,
      className,
      ariaLabel = "Pagination",
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const singlePage = totalPages <= 1;

    return (
      <nav
        aria-label={ariaLabel}
        className={cn("flex items-center justify-end gap-2", className)}
        ref={ref}
      >
        {totalCount != null && (
          <span className="mr-auto type-body-xs">
            {t("common.pagination.totalCount", { count: totalCount.toLocaleString() })}
          </span>
        )}

        <Button
          aria-label={t("common.pagination.previousPage")}
          disabled={disabled || singlePage || !hasPrevious}
          size="sm"
          StartIcon={ChevronLeft}
          variant="ghost"
          onClick={() => onPageChange(pageNumber - 1)}
        >
          {t("common.previous")}
        </Button>

        <span className="type-body-xs">{`${pageNumber} / ${totalPages}`}</span>

        <Button
          aria-label={t("common.pagination.nextPage")}
          disabled={disabled || singlePage || !hasNext}
          EndIcon={ChevronRight}
          size="sm"
          variant="ghost"
          onClick={() => onPageChange(pageNumber + 1)}
        >
          {t("common.next")}
        </Button>
      </nav>
    );
  },
);

Pagination.displayName = "Pagination";
